/**
 * Creates a singleton function that caches the result of the first invocation.
 * Subsequent calls return the cached value without re-executing the function.
 *
 * @param func - Function to execute once and cache the result
 * @returns A function that returns the cached result
 *
 * @example
 * ```ts
 * import { CreateSingleton } from "@pdfking/tfunc"
 *
 * const expensiveOperation = () => {
 *   console.log('Computing...');
 *   return 42;
 * };
 *
 * const getSingleton = CreateSingleton(expensiveOperation);
 *
 * console.log(getSingleton()); // Logs: "Computing..." then returns 42
 * console.log(getSingleton()); // Returns 42 (no log, uses cached value)
 * console.log(getSingleton()); // Returns 42 (no log, uses cached value)
 * ```
 */
export const CreateSingleton = <T>(func: () => T): (() => T) => {
  let res: T
  return () => res ?? (res = func())
}
